import { Node } from "@tiptap/core";
import { VueNodeViewRenderer, useEditor } from "@tiptap/vue-3";
import ImageNode from "@/components/nodes/Image.vue";
import {
  useImageUploadStore,
  useImageUploader,
} from "~/composables/image-upload";

export const ImageUploadState = ref({});

export function useHandleDrop() {
  const imageStore = useImageUploadStore();
  const upload = useImageUploader();

  return function handleDrop(
    ...[view, event, slice, moved]: Parameters<
      NonNullable<
        NonNullable<
          NonNullable<Parameters<typeof useEditor>[0]>["editorProps"]
        >["handleDrop"]
      >
    >
  ): boolean {
    if (
      !moved &&
      event.dataTransfer &&
      event.dataTransfer.files &&
      event.dataTransfer.files[0]
    ) {
      // if dropping external files
      let file = event.dataTransfer.files[0]; // the dropped file

      const { schema } = view.state;
      const coordinates = view.posAtCoords({
        left: event.clientX,
        top: event.clientY,
      });
      if (!coordinates) throw new Error("not sure where to place");

      const ref = upload(file);

      const node = schema.nodes.image.create({ uploadRef: ref }); // creates the image element
      const transaction = view.state.tr.insert(coordinates?.pos, node); // places it in the correct position

      view.dispatch(transaction);

      return true; // handled
    }
    return false;
  };
}

function sizeTo(mediaUrl: string, query: object) {
  const url = new URL(mediaUrl);
  if (url.pathname.startsWith("/storage/v1/object/public/")) {
    url.pathname = url.pathname.replace(
      /^\/storage\/v1\/object\/public\//,
      "/storage/v1/render/image/public/"
    );
    for (const [key, value] of Object.entries(query)) {
      url.searchParams.set(key, value);
    }

    return url.href;
  }

  return mediaUrl;
}

export default Node.create({
  name: "image",
  group: "block",
  atom: true,
  renderHTML({ node, HTMLAttributes }) {
    return [
      "div",
      {
        class: `image ${node.attrs.mode} placeholder`,
      },
      [
        "img",
        {
          src: sizeTo(HTMLAttributes.href, { width: 20, resize: "contain" }),
          class: `${node.attrs.mode}`,
          width: node.attrs.width,
          height: node.attrs.height,
        },
      ],
    ];
  },
  addAttributes() {
    return {
      uploadRef: { default: null },
      href: { default: null },
      bucket: { default: null },
      key: { default: null },
      mode: { default: null },
      width: { default: null },
      height: { default: null },
    };
  },
  addNodeView() {
    return VueNodeViewRenderer(ImageNode);
  },
});
